import {
  BboxRect,
  GroupedElements,
  GroupedSmartSurveyorElement,
} from "components/SmartSurveyor/ReviewPage/types";
import { generateDistinctColors } from "utils/generateDistinctColors";
import { computeCoordinateExtents } from "utils/math/computeCoordinateExtents";
import { routeGroupLabelOf } from "./routeGroupLabelOf";
import {
  DEFAULT_ELEMENT_MULTIPLIER,
  DEFAULT_MAP_ELEMENT_RGB,
} from "common/constants";
import type {
  MarkerNote,
  ParkingGroundCounts,
  Route,
  Node,
  Edge,
  ScaledRoute,
} from "../SmartSurveyorForRudolfFritz/utils/types";
import type { PlanRouteGroupV3, PlanV3 } from "../SmartSurveyorV3/utils/types";

function markerToGroupedElement(
  marker: {
    id?: string;
    page?: number;
    bbox: BboxRect;
    multiplier?: number;
    note?: MarkerNote | null;
  },
  groupName: string,
  floorPlanImage: string,
  scaleFactor: number,
  rgb: [number, number, number],
): GroupedSmartSurveyorElement {
  return {
    id:
      marker.id ??
      `${groupName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    layer: "",
    rgb,
    txt: "",
    group_id: groupName,
    grid_cols: [],
    grid_rows: [],
    position_description: "",
    bbox: marker.bbox,
    // Only defaulted here: the structured-data payload is untyped on the backend.
    multiplier: marker.multiplier ?? DEFAULT_ELEMENT_MULTIPLIER,
    page: marker.page,
    status: "installed",
    floorPlanImage,
    _scaleFactor: scaleFactor,
    note: marker.note ?? null,
  };
}

function resolveScaleFactor(
  scaleFactor: number | Map<number, number>,
  pageIndex: number,
): number {
  if (typeof scaleFactor === "number") return scaleFactor;
  return scaleFactor.get(pageIndex) ?? 1;
}

function buildGroupColorMap(
  groups: ParkingGroundCounts["groups"],
): Map<string, [number, number, number]> {
  const groupNames = (groups ?? []).map((group) => group.name ?? "(No Group)");
  const uniqueGroupNames = [...new Set(groupNames)];
  const colors = generateDistinctColors({ count: uniqueGroupNames.length });
  const colorByGroup = new Map<string, [number, number, number]>();
  for (let index = 0; index < uniqueGroupNames.length; index++) {
    colorByGroup.set(uniqueGroupNames[index], colors[index]);
  }
  return colorByGroup;
}

export function groupedElementsFromCounts(
  file: ParkingGroundCounts,
  scaleFactor: number | Map<number, number>,
): GroupedElements {
  const colorByGroup = buildGroupColorMap(file.groups);

  const byGroupId: Record<string, GroupedSmartSurveyorElement[]> = {};
  let total = 0;

  for (const group of file.groups ?? []) {
    const groupName = group.name ?? "(No Group)";
    const list = (byGroupId[groupName] ||= []);
    const rgb = colorByGroup.get(groupName) ?? DEFAULT_MAP_ELEMENT_RGB;

    for (const marker of group.markers ?? []) {
      const pageIndex = marker.page ?? 0;
      const pageImage =
        file.pages?.[pageIndex]?.image ?? file.pdf_filename ?? "";

      // Only backend-signed CloudFront URLs are usable; unsigned keys stay unreachable.
      const floorPlanImageUrl = pageImage.startsWith("http") ? pageImage : "";

      const sf = resolveScaleFactor(scaleFactor, pageIndex);
      const scaledBbox: BboxRect = {
        x1: marker.bbox.x1 * sf,
        y1: marker.bbox.y1 * sf,
        x2: marker.bbox.x2 * sf,
        y2: marker.bbox.y2 * sf,
      };
      const scaledMarker = { ...marker, bbox: scaledBbox };

      const groupedElement = markerToGroupedElement(
        scaledMarker,
        groupName,
        floorPlanImageUrl,
        sf,
        rgb,
      );
      list.push(groupedElement);
      total += groupedElement.multiplier;
    }
  }

  // Left unsorted: the caller orders these itself, so sorting here is discarded.
  const groupIds = Object.keys(byGroupId);

  const result: GroupedElements = {
    all: { label: "all_groups", count: total },
    byGroupId,
    groupIds,
  };

  return result;
}

export function buildRouteGroupsForPlan(
  routes: Route[],
  scaleFactor: number | Map<number, number>,
): PlanRouteGroupV3[] {
  const byCable: Record<string, { meters: number; routes: ScaledRoute[] }> = {};

  for (const r of routes) {
    const cable = routeGroupLabelOf(r.cable_type);
    const meters =
      parseFloat(String(r.meters ?? "").replace(/[^0-9.-]/g, "")) || 0;

    const nodesSrc: Node[] = r.nodes ?? [];
    if (nodesSrc.length === 0) continue;

    const sf = resolveScaleFactor(scaleFactor, r.page ?? 0);
    const scaledNodes = nodesSrc.map((n) => ({
      id: n.id,
      x: (n.x ?? 0) * sf,
      y: (n.y ?? 0) * sf,
    }));
    const edgesSrc: Edge[] = r.edges ?? [];

    const scaled: ScaledRoute = {
      route_type_id: r.route_type_id,
      id: r.id,
      name: r.name,
      page: r.page,
      breaker: r.breaker,
      cable_type: r.cable_type,
      cable_type_status: r.cable_type_status,
      lv_position: r.lv_position,
      lvMatch: r.lvMatch ?? undefined,
      note: r.note,
      distance_px: r.distance_px,
      meters,
      nodes: scaledNodes,
      edges: edgesSrc,
      creation_source: r.creation_source,
      coordinateExtents: computeCoordinateExtents(scaledNodes),
    };

    const entry = (byCable[cable] ??= { meters: 0, routes: [] });
    entry.meters += meters;
    entry.routes.push(scaled);
  }

  const labels = Object.keys(byCable);
  const colors = generateDistinctColors({ count: labels.length });

  return labels.map((label, index) => ({
    route_type_id: byCable[label].routes[0]?.route_type_id!,
    label,
    meters: byCable[label].meters,
    routes: byCable[label].routes,
    rgb: colors[index],
    // Take cableTypeStatus from the first route in the group
    cableTypeStatus: byCable[label].routes[0].cable_type_status,
  }));
}

export function aggregateRouteGroups(
  plans: PlanV3[],
): { label: string; meters: number; floors: any[] }[] {
  const map: Record<string, { label: string; meters: number; floors: any[] }> =
    {};

  for (const p of plans) {
    for (const rg of p.routeGroups ?? []) {
      const lbl = rg.label ?? "(no-type)";
      const entry = (map[lbl] ??= { label: lbl, meters: 0, floors: [] });
      entry.meters += rg.meters ?? 0;
      entry.floors.push({
        planIndex: p.index,
        planLabel: p.label,
        planImage: p.planImage,
        meters: rg.meters ?? 0,
        routes: rg.routes.slice(),
      });
    }
  }

  return Object.values(map);
}
